import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import TrailCard from '@alltrails/shared/components/TrailCard';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { trailFromAlgoliaTrail, TrailActivityType } from '@alltrails/trails';
import { TrailSearchResult } from '@alltrails/search/types/algoliaResultTypes';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Carousel from '@alltrails/denali/components/Carousel';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import ExploreMapPinType from '@alltrails/amplitude/enums/ExploreMapPinType';
import { useAppSelector } from '../../redux';
import PopupCarouselControls from '../PopupCarouselControls';
import PaddingAwarePopup from '../PaddingAwarePopup';
import styles from './styles/ActiveTrailPopup.module.scss';

type ActiveTrailPopupProps = Pick<ComponentProps<typeof PaddingAwarePopup>, 'latitude' | 'longitude' | 'offset'> & {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  initialTrailIndex?: number;
  isComplete: (id: number) => boolean;
  isFavorite: (id: number) => boolean;
  isVerified: (id: number) => boolean;
  onFavoriteClick: (id: number) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTrailCardViewed?: (id: number, index: number) => void;
  onTrailChange: (trail: TrailSearchResult) => void;
  trails: TrailSearchResult[];
};

const ActiveTrailPopup = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  initialTrailIndex = 0,
  isComplete,
  isFavorite,
  isVerified,
  latitude,
  longitude,
  offset,
  onFavoriteClick,
  onMouseEnter,
  onMouseLeave,
  onTrailChange,
  onTrailCardViewed,
  trails
}: ActiveTrailPopupProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { hasPermission } = useAuthorization();
  const languageRegionCode = useLanguageRegionCode();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const features = useFeatures();
  const isRoundEstTimeToCompletionEnabled = features?.includes('round_est_time_completion');

  const [index, setIndex] = useState(initialTrailIndex);
  useEffect(() => {
    setIndex(initialTrailIndex);
  }, [initialTrailIndex]);

  const onIndexChange = useCallback(
    (newIndex: number) => {
      const newTrail = trails[newIndex];
      setIndex(newIndex);
      onTrailChange(newTrail);
      let pinType: ExploreMapPinType = 'trailhead';
      if (trails.every(t => t.is_closed)) {
        pinType = 'closed';
      } else if (trails.every(t => isFavorite(t.ID))) {
        pinType = 'saved';
      } else if (trails.every(t => isComplete(t.ID))) {
        pinType = 'completed';
      }

      logExploreMapPinSelected({
        action_taken: 'toggled from trailhead pin',
        pin_type: pinType,
        trail_id: newTrail.ID
      });
    },
    [isComplete, isFavorite, onTrailChange, trails]
  );

  const getAdminCTAs = useCallback(
    (trailId: number) => {
      if (hasPermission({ permission: 'trails:manage' })) {
        return [
          {
            id: 'edit',
            label: intl.formatMessage({ defaultMessage: 'Edit' }),
            onClick: () => window.open(`/contribute/route?trail_id=${trailId}`, '_blank')
          }
        ];
      }
      return undefined;
    },
    [hasPermission, intl]
  );

  const onCardClick = useCallback(() => {
    logTrailCardClicked({ trail_id: String(trails[index].id), card_location: cardLocation, detailed_card_location: detailedCardLocation });
  }, [cardLocation, detailedCardLocation, index, trails]);

  const renderTrailCard = useCallback(
    (result: TrailSearchResult) => (
      <TrailCard
        key={result.ID} // use a key to ensure the correct trail photo is used if the shown trail is changed via hovering fast enough that the TrailCard is still rendered
        adminCTAs={getAdminCTAs(result.ID)}
        className={styles.trailCard}
        variant="small"
        languageRegionCode={languageRegionCode}
        onCardMouseEnter={onMouseEnter}
        onCardMouseLeave={onMouseLeave}
        onFavoriteClick={() => onFavoriteClick(result.ID)}
        onCardClick={onCardClick}
        isFavorite={isFavorite(result.ID)}
        isCompleted={isComplete(result.ID)}
        isVerified={isVerified(result.ID)}
        trail={trailFromAlgoliaTrail(result, activityType)}
        useMetric={displayMetric}
        lazyLoadImages={true}
        openInNewTab
        isRoundEstTimeToCompletionEnabled={isRoundEstTimeToCompletionEnabled}
      />
    ),
    [
      activityType,
      displayMetric,
      getAdminCTAs,
      isComplete,
      isFavorite,
      isRoundEstTimeToCompletionEnabled,
      isVerified,
      languageRegionCode,
      onFavoriteClick,
      onCardClick,
      onMouseEnter,
      onMouseLeave
    ]
  );

  const trail = trails?.[index];

  useEffect(() => {
    onTrailCardViewed?.(trail?.ID, index);
    // Only want to log this when the trail changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trail?.ID]);

  if (!trail) {
    return null;
  }

  if (isMobile) {
    return (
      <div className={styles.mobilePopup}>
        <Carousel
          uniqueId="trail-pin-carousel"
          items={trails}
          renderItem={renderTrailCard}
          getItemKey={trail => `trail-${trail.ID}`}
          initialSlideIndex={index}
          onSlideChange={onIndexChange}
          showPaginationDots={false}
          slidesPerViewMobile={1}
        />
      </div>
    );
  }

  return (
    <PaddingAwarePopup
      className={styles.desktopPopup}
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={latitude}
      longitude={longitude}
      maxWidth="none"
      offset={offset}
    >
      <div className={styles.desktopPopupContents}>
        {renderTrailCard(trail)}
        {trails.length > 1 ? (
          <PopupCarouselControls className={styles.desktopPopupControls} index={index} onIndexChange={onIndexChange} total={trails.length} />
        ) : null}
      </div>
    </PaddingAwarePopup>
  );
};

export default ActiveTrailPopup;
